import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';

import { AggregatedStatus } from '@vi-iot/client';
import { accordionAnimation, arrowRotateAnimation } from '@shared-ui';

import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { GTMService, UIService } from '@services';
import {
  ALLOWED_DIAGNOSIS_IDS,
  AnomalyLevel,
  DevicesStore,
  InstallationDetailsStore,
  RemoteServiceDiagnosisEntry,
  RemoteServiceDiagnosisLoadingStatus,
  RemoteServiceStore,
} from '@store';

import { RemoteServiceOverlayTypeEnum } from '../../installation-overlay.model';

import { matTooltipSetter } from '../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { RemoteServiceService } from './../../remote-service/remote-service.service';
import { DashboardAnomalyPillHelper } from 'src/app/+kanban-board/kanban-installation-list/pipes/dashboard-anomaly-pill/dashboard-anomaly-pill.helper';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';

@Component({
  selector: 'app-remote-service-card',
  templateUrl: './remote-service-card.component.html',
  styleUrls: ['./remote-service-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [arrowRotateAnimation, accordionAnimation],
})
export class RemoteServiceCardComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  MAX_COLLAPSED_ENTRIES = 2;
  AggregatedStatus = AggregatedStatus;
  gatewaySerial: string;
  installationId: number;
  RemoteServiceDiagnosisLoadingStatus = RemoteServiceDiagnosisLoadingStatus;
  isExpanded = false;
  canDisplayAllEntries = false;

  anomaliesList: RemoteServiceDiagnosisEntry[] = [];
  diagnosisCalculationDataStatus: RemoteServiceDiagnosisLoadingStatus;
  isPermanentInspectionEntriesVisible$: Observable<boolean>;
  anomaliesWithMaintainerFreeOrBasicOrPlusPackage: RemoteServiceDiagnosisEntry[] = [];
  isMaintainerWithFreeOrBasicOrPlusPackage = false;
  isE3HeatpumpDevice$: Observable<boolean>;

  private deviceSerial: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public remoteServiceService: RemoteServiceService,
    public remoteServiceStore: RemoteServiceStore,
    public uiService: UIService,
    public installationDetailsStore: InstallationDetailsStore,
    public deviceStore: DevicesStore,
    protected customTranslationsService: CustomTranslationsService,
    private activatedRoute: ActivatedRoute,
    private gtmService: GTMService
  ) {}

  ngOnInit(): void {
    this.remoteServiceStore.clearDetailedInspectionState();

    this.installationDetailsStore.isMaintainerWithFreeOrBasicOrPlusPackage$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isMaintainerWithFreeOrBasicOrPlusPackage) => {
        this.isMaintainerWithFreeOrBasicOrPlusPackage = isMaintainerWithFreeOrBasicOrPlusPackage;
      });

    this.remoteServiceStore.diagnosisList$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((anomalies) => {
        this.anomaliesList = anomalies || [];

        this.anomaliesList = this.anomaliesList
          .filter((entry) => entry.diagnosis_anomaly === AnomalyLevel.Anomaly)
          .sort(
            (a, b) =>
              DashboardAnomalyPillHelper.AnomalySeveritySortValue[a.diagnosis_priority] -
              DashboardAnomalyPillHelper.AnomalySeveritySortValue[b.diagnosis_priority]
          );

        if (this.isMaintainerWithFreeOrBasicOrPlusPackage && anomalies?.length > 0) {
          this.anomaliesWithMaintainerFreeOrBasicOrPlusPackage = anomalies.filter((anomaly) =>
            ALLOWED_DIAGNOSIS_IDS.includes(anomaly.diagnosis_id)
          );
        }
      });

    this.installationDetailsStore.getInstallationId$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((installationId) => {
        this.installationId = installationId;
      });
    this.remoteServiceStore.diagnosisCalculationDataStatus$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((dataStatus) => {
        this.diagnosisCalculationDataStatus = dataStatus;
      });

    this.activatedRoute.queryParamMap.subscribe((queryParam) => {
      this.gatewaySerial = queryParam.get('gatewaySerial');
      this.deviceSerial = queryParam.get('deviceSerial');
      this.isPermanentInspectionEntriesVisible$ = this.installationDetailsStore.isPermanentInspectionEntriesVisible(
        this.gatewaySerial
      );

      this.remoteServiceStore.loadDiagnosisData(this.deviceSerial);
    });

    this.isE3HeatpumpDevice$ = this.remoteServiceService.getIsE3HeatpumpDevice();
  }

  getRemoteServicePrefix(key: string): string {
    if (key) {
      return `+REMOTE_SERVICE.${key.toUpperCase()}`;
    }

    return key;
  }

  navigateToSelectedAnomaly(anomaly: RemoteServiceDiagnosisEntry): void {
    this.remoteServiceStore.selectDiagnosisEntry(anomaly);
    if (this.isMaintainerWithFreeOrBasicOrPlusPackage) {
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Diagnosis,
        this.installationId,
        this.gatewaySerial
      );
    } else {
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Anomalies,
        this.installationId,
        this.gatewaySerial
      );
    }
  }

  navigateToAnomalies(): void {
    if (this.isMaintainerWithFreeOrBasicOrPlusPackage) {
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Diagnosis,
        this.installationId,
        this.gatewaySerial
      );
    } else {
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Anomalies,
        this.installationId,
        this.gatewaySerial
      );
    }
    this.gtmService.track(
      'onOpen',
      'Open remoteServiceAnomalies',
      'permanent inspection card in sidenav'
    );
  }

  canDisplayDiagnosisEntry(index: number): boolean {
    return this.canDisplayAllEntries ? true : index < this.MAX_COLLAPSED_ENTRIES;
  }

  isMoreButtonVisible(): boolean {
    const manyEntries = this.anomaliesList.length > this.MAX_COLLAPSED_ENTRIES;

    return manyEntries;
  }

  showLess(): void {
    this.canDisplayAllEntries = false;
  }

  showMore(): void {
    this.canDisplayAllEntries = true;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
